import type { Decimal } from '@prisma/client/runtime/library';

export const fractionPattern = '(\\d+)\\W*((\\d+)\\/(\\d+))?';
export const fractionRegex = new RegExp(fractionPattern);

/**
 * Unicode vulgar fractions (U+00BC-U+00BE, U+2150-U+215E, U+2189) mapped to their ASCII spelling.
 * These arrive via paste from PDFs and Word, and via iOS/macOS text substitution.
 */
export const unicodeFractionMap: Record<string, string> = {
	'↉': '0/3',
	'⅒': '1/10',
	'⅑': '1/9',
	'⅛': '1/8',
	'⅐': '1/7',
	'⅙': '1/6',
	'⅕': '1/5',
	'¼': '1/4',
	'⅓': '1/3',
	'½': '1/2',
	'⅖': '2/5',
	'⅔': '2/3',
	'⅜': '3/8',
	'⅗': '3/5',
	'¾': '3/4',
	'⅘': '4/5',
	'⅝': '5/8',
	'⅚': '5/6',
	'⅞': '7/8',
	'⅟': '1/',
};

const superscriptDigits = '⁰¹²³⁴⁵⁶⁷⁸⁹';
const subscriptDigits = '₀₁₂₃₄₅₆₇₈₉';

const unicodeFractionGlyphs = Object.keys(unicodeFractionMap).join('');

// A vulgar fraction directly after a digit needs a separator, so "24⅝" becomes "24 5/8" rather than "245/8".
const digitThenGlyphRegex = new RegExp(`(\\d)\\s*([${unicodeFractionGlyphs}])`, 'g');
const glyphRegex = new RegExp(`[${unicodeFractionGlyphs}]`, 'g');
const scriptDigitRegex = new RegExp(`[${superscriptDigits}${subscriptDigits}]`, 'g');

/**
 * Rewrites unicode fraction notation into the ASCII form the fraction parsers understand:
 * vulgar fractions (⅝ → 5/8), fraction/division slashes (5⁄8 → 5/8), super/subscript digits (⁵⁄₈ → 5/8),
 * and any exotic whitespace (em space, non-breaking space, tab) separating a whole number from its
 * fraction. Input already in ASCII form is returned unchanged.
 *
 * Every whitespace character strictFractionRegex accepts must collapse to an ASCII space here, because
 * parseFraction picks its parser by testing for a literal ' '. Leave one uncollapsed and a value can
 * validate but then parse as 0 — worse than being rejected outright.
 */
export const normalizeUnicodeFractions = (entry: string): string =>
	entry
		.replace(/\s/g, ' ')
		.replace(/[⁄∕]/g, '/')
		.replace(scriptDigitRegex, (digit) => {
			const superscriptIndex = superscriptDigits.indexOf(digit);
			return String(superscriptIndex !== -1 ? superscriptIndex : subscriptDigits.indexOf(digit));
		})
		.replace(digitThenGlyphRegex, (_match, digit: string, glyph: string) => `${digit} ${unicodeFractionMap[glyph]}`)
		.replace(glyphRegex, (glyph) => unicodeFractionMap[glyph]!);

/**
 * Anchored counterpart to fractionRegex, for validating user input rather than extracting from it.
 * fractionRegex is unanchored with an optional fraction group, so it matches any string containing a
 * digit — it cannot tell "24 5/8" from "24.5" or "abc 3 xyz". Accepts a whole number, a fraction, or
 * both, with an optional inch mark. Decimals are rejected: these fields take fractions.
 */
export const strictFractionPattern = "^\\s*(?:\\d+\\s*[-\\s]\\s*\\d+\\s*\\/\\s*\\d+|\\d+\\s*\\/\\s*\\d+|\\d+)\\s*(?:\"|'')?\\s*$";
export const strictFractionRegex = new RegExp(strictFractionPattern);

export const isFractionString = ({ entry }: { entry: string }): boolean => strictFractionRegex.test(normalizeUnicodeFractions(entry));

type FractionFormat = {
	separator: ' ' | '-';
	suffix: '' | '"';
};

const allFractionFormats: FractionFormat[] = [
	{ separator: ' ', suffix: '' },
	{ separator: ' ', suffix: '"' },
	{ separator: '-', suffix: '' },
	{ separator: '-', suffix: '"' },
];

class Fraction {
	denominator: number | undefined;
	numerator: number | undefined;
	whole: number | undefined;

	private frac: string | undefined;
	private hasBoth: boolean;

	constructor({ denominator, numerator, whole }: { denominator?: number; numerator?: number; whole?: number }) {
		this.denominator = denominator;
		this.numerator = numerator;
		this.whole = whole;

		this.frac = this.numerator !== undefined && this.denominator !== undefined ? `${this.numerator}/${this.denominator}` : undefined;
		this.hasBoth = this.whole !== undefined && this.frac !== undefined;
	}

	toString(format: FractionFormat = { separator: ' ', suffix: '' }) {
		return `${this.whole !== undefined ? this.whole : ''}${this.hasBoth ? format.separator : ''}${this.frac ?? ''}${format.suffix}`.trim();
	}

	toAllFormats() {
		return allFractionFormats.map((format) => this.toString(format));
	}
}

export const parseFraction: (entry: string | null | Decimal) => Fraction | undefined = (entry) => {
	if (!entry) return undefined;

	if (typeof entry !== 'string') entry = entry.toString();

	entry = normalizeUnicodeFractions(entry);

	// If there is no space, but there is a slash in the entry, we assume it is just a fraction.
	if (!(entry.includes(' ') || entry.includes('-')) && entry.includes('/')) return parseFractionWithoutWhole(entry);

	const match = fractionRegex.exec(entry);
	if (!match) {
		return undefined;
	}
	const parseMaybe = (maybe?: string) => (maybe ? +maybe : undefined);
	const whole = parseMaybe(match[1]),
		numerator = parseMaybe(match[3]),
		denominator = parseMaybe(match[4]);

	return new Fraction({
		denominator,
		numerator,
		whole,
	});
};

export const fractionWithoutWholePattern = '((\\d+)\\/(\\d+))?';
export const fractionWithoutWholeRegex = new RegExp(fractionWithoutWholePattern);

export const parseFractionWithoutWhole: (entry: string) => Fraction | undefined = (entry) => {
	const match = fractionWithoutWholeRegex.exec(normalizeUnicodeFractions(entry));
	if (!match) {
		return undefined;
	}
	const parseMaybe = (maybe?: string) => (maybe ? +maybe : undefined);
	const numerator = parseMaybe(match[2]),
		denominator = parseMaybe(match[3]);
	return new Fraction({
		numerator,
		denominator,
	});
};

export const fractionToDecimal: (fraction: Fraction) => number = (fraction) => {
	return (fraction.whole ?? 0) + (!!fraction.numerator && !!fraction.denominator ? fraction.numerator / fraction.denominator : 0);
};

export const parseFractionToDecimal: (entry: string | null) => number | undefined = (entry) => {
	const fraction = parseFraction(entry);
	return fraction === undefined ? undefined : fractionToDecimal(fraction);
};

type ImperialPrecision = 2 | 4 | 8 | 16 | 32 | 64;

export function decimalToFraction({
	decimal,
	precision = 32,
	setZeroAsUndefined,
}: {
	decimal: number;
	precision?: ImperialPrecision;
	setZeroAsUndefined?: boolean;
}) {
	const whole = Math.floor(decimal);
	const remainder = decimal - whole;
	let denominator = precision as number;
	let numerator = Math.round(remainder * precision);
	if (numerator == 0) {
		return new Fraction({
			whole,
		});
	}
	// In the case of .999... we need to round up
	if (numerator === denominator) {
		return new Fraction({
			whole: whole + 1,
		});
	}
	while (denominator > 2 && numerator % 2 == 0) {
		denominator = denominator / 2;
		numerator = numerator / 2;
	}
	return new Fraction({
		// Whole is hidden in toString if set to undefined, but shows up as 0 if set to 0
		whole: whole !== 0 ? whole : setZeroAsUndefined ? undefined : 0,
		numerator,
		denominator,
	});
}

export const fractionStringToDecimalString = (fractionString: string) => {
	const match = fractionRegex.exec(normalizeUnicodeFractions(fractionString));
	if (!match) {
		return undefined;
	} else {
		const parsedFraction = parseFraction(fractionString);
		if (!parsedFraction) {
			return undefined;
		}
		return String(fractionToDecimal(parsedFraction));
	}
};

/**
 * Parses a measurement string in various formats and returns the value in inches.
 * Handles:
 * - Inches only: "18"", "1-3/4""
 * - Feet-inches: "1'-6"", "7'-0""
 * - Malformed feet-inches: "1'-6'" (wrong inch mark)
 */
export function parseToInches(entry: string | null): number | undefined {
	if (!entry) return undefined;

	// Strip a trailing slash with no denominator (e.g. "82 /" → "82")
	// Also normalise '' (two single-quotes used as an inch mark) → "
	const cleaned = normalizeUnicodeFractions(entry)
		.trim()
		.replace(/\s*\/\s*$/, '')
		.replace(/''/g, '"');

	// Check if it's feet-inches format (contains foot mark)
	// Split on first ' only, in case inches also mistakenly uses '
	const footMarkIndex = cleaned.indexOf("'");

	if (footMarkIndex !== -1) {
		const feetPart = cleaned.slice(0, footMarkIndex);
		const inchesPart = cleaned.slice(footMarkIndex + 1);

		const feet = parseInt(feetPart, 10) || 0;

		// Clean inches: remove leading dash/space and any trailing marks (' " ″ ′)
		const inchesClean = inchesPart.replace(/^[-\s]+/, '').replace(/['′""″]$/, '');

		const inches = parseFractionToDecimal(inchesClean) ?? 0;

		return feet * 12 + inches;
	}

	// Just inches - remove trailing marks
	const withoutMark = cleaned.replace(/['′""″]$/, '');
	return parseFractionToDecimal(withoutMark);
}
